import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, EventSchedule } from '../lib/api-client';
import { translationClient } from '../lib/translation';
import type { StreamerProfile } from '../lib/types';
import { resolveMediaUrl } from '../lib/media-url';

// Translated profile fields
interface TranslatedProfile {
  hobbies?: string;
  favorite_food?: string;
  features?: string;
  bio?: string;
  notice_prefix?: string;
}

interface ProfileTabProps {
  username: string;
}

// Format schedule date/time for display
function formatScheduleDateTime(isoString: string, t: (key: string) => string): string {
  const date = new Date(isoString);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const weekdayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const weekday = t(`profile.weekdays.${weekdayKeys[date.getDay()]}`);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${month}/${day} (${weekday}) ${hours}:${minutes}`;
}

// Format schedule time range
function formatScheduleTimeRange(start: string, end: string, t: (key: string) => string): string {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const startFormatted = formatScheduleDateTime(start, t);
  const endHours = String(endDate.getHours()).padStart(2, '0');
  const endMinutes = String(endDate.getMinutes()).padStart(2, '0');

  // If same day, show only end time
  if (startDate.toDateString() === endDate.toDateString()) {
    return `${startFormatted}〜${endHours}:${endMinutes}`;
  }
  // Different day, show full end date
  return `${startFormatted}〜${formatScheduleDateTime(end, t)}`;
}

export default function ProfileTab({ username }: ProfileTabProps) {
  const { t, i18n } = useTranslation('watch');
  const [profile, setProfile] = useState<StreamerProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [noticePrefix, setNoticePrefix] = useState<string>('');
  const [schedules, setSchedules] = useState<EventSchedule[]>([]);
  const [translatedProfile, setTranslatedProfile] = useState<TranslatedProfile>({});
  const [isTranslating, setIsTranslating] = useState<boolean>(false);
  const [isTranslationEnabled, setIsTranslationEnabled] = useState<boolean>(false);

  // Check if we need to translate (English mode AND translation enabled)
  const needsTranslation = i18n.language === 'en' && isTranslationEnabled;

  // Check translation settings on mount (using public endpoint)
  useEffect(() => {
    const checkTranslationSettings = async () => {
      try {
        const isConfigured = await translationClient.isConfiguredPublic();
        setIsTranslationEnabled(isConfigured);
        console.log('[ProfileTab] Translation enabled:', isConfigured);
      } catch (err) {
        console.error('[ProfileTab] Failed to check translation settings:', err);
        setIsTranslationEnabled(false);
      }
    };
    checkTranslationSettings();
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!username) return;

      setIsLoading(true);
      setError(null);

      try {
        // Fetch the profile directly by username
        console.log('[ProfileTab] Fetching profile for username:', username);
        const data = await apiClient.getStreamerProfile(username);
        console.log('[ProfileTab] Profile data received:', data);
        setProfile(data);

        // Fetch schedules and notice prefix separately to not fail if one fails
        if (data.user_id) {
          try {
            const scheduleResponse = await apiClient.listEventSchedules({
              streamer_id: data.user_id,
              upcoming_only: true,
              limit: 10,
            });
            setSchedules(scheduleResponse.schedules);
          } catch (scheduleErr) {
            console.error('[ProfileTab] Error fetching schedules:', scheduleErr);
            // Don't fail the whole profile, just skip schedules
          }

          try {
            const prefixResponse = await apiClient.getScheduleNoticePrefix(data.user_id);
            setNoticePrefix(prefixResponse.value || '');
          } catch (prefixErr) {
            console.error('[ProfileTab] Error fetching notice prefix:', prefixErr);
            // Don't fail the whole profile, just skip prefix
          }
        }
      } catch (err) {
        console.error('[ProfileTab] Error fetching profile:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch profile'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [username]);

  // Translate profile content when in English mode (with server-side caching)
  useEffect(() => {
    const translateProfile = async () => {
      if (!needsTranslation || !profile || !profile.user_id) {
        setTranslatedProfile({});
        return;
      }

      // Collect non-empty fields to translate (including noticePrefix)
      const fields: Record<string, string> = {};
      if (profile.hobbies) fields['hobbies'] = profile.hobbies;
      if (profile.favorite_food) fields['favorite_food'] = profile.favorite_food;
      if (profile.features) fields['features'] = profile.features;
      if (profile.bio) fields['bio'] = profile.bio;
      if (noticePrefix) fields['notice_prefix'] = noticePrefix;

      if (Object.keys(fields).length === 0) {
        setTranslatedProfile({});
        return;
      }

      setIsTranslating(true);
      try {
        // Use profile translate endpoint with server-side caching
        const result = await translationClient.translateProfilePublic(
          profile.user_id,
          fields,
          'EN'
        );

        console.log('[ProfileTab] Translated profile:', result.translations,
          `(cached: ${result.cached_count}, translated: ${result.translated_count})`);
        setTranslatedProfile(result.translations);
      } catch (err) {
        console.error('[ProfileTab] Translation error:', err);
        // On error, clear translations (will show original text)
        setTranslatedProfile({});
      } finally {
        setIsTranslating(false);
      }
    };

    translateProfile();
  }, [profile, noticePrefix, needsTranslation]);

  // Helper to get display value (translated or original)
  const getDisplayValue = (key: keyof TranslatedProfile, original: string | null | undefined): string => {
    if (!original) return t('profile.notSet');
    if (needsTranslation && translatedProfile[key]) {
      return translatedProfile[key]!;
    }
    return original;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('profile.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">{t('profile.fetchError')}</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('profile.noProfile')}</div>
      </div>
    );
  }

  // Build profile image URL
  const profileImageUrl = profile.profile_image_url
    ? resolveMediaUrl(profile.profile_image_url)
    : null;

  return (
    <div className="space-y-6">
      {/* Profile Header Section */}
      <div className="flex items-center justify-start gap-4 pb-6 border-b border-gray-200">
        <div className="w-20 h-20 rounded-full overflow-hidden flex-shrink-0 bg-gray-200">
          {profileImageUrl ? (
            <img
              src={profileImageUrl}
              alt={profile.display_name || profile.username}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-4xl">👩</div>
          )}
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {profile.display_name || profile.username}
          </h2>
        </div>
      </div>

      {/* Profile Details Section */}
      <div className="flex justify-start items-start gap-4">
        {/* Profile Detail Image */}
        {profileImageUrl && (
          <div className="w-48 flex-shrink-0">
            <button
              onClick={() => setShowImageModal(true)}
              className="w-full cursor-pointer group"
            >
              <img
                src={profileImageUrl}
                alt={profile.display_name || profile.username}
                className="w-full h-auto object-cover transition-transform group-hover:scale-105"
              />
            </button>
          </div>
        )}

        {/* Profile Details Grid - mock-htmlに合わせたフィールドのみ */}
        <div className="flex-1 grid grid-cols-[120px_1fr] gap-y-3 gap-x-4 text-sm text-left">
          <div className="text-gray-500 font-semibold text-left">{t('profile.age')}</div>
          <div className="text-gray-900 text-left">{profile.age ? t('profile.ageValue', { age: profile.age }) : t('profile.notSet')}</div>

          <div className="text-gray-500 font-semibold text-left">{t('profile.height')}</div>
          <div className="text-gray-900 text-left">{profile.height ? t('profile.heightValue', { height: profile.height }) : t('profile.notSet')}</div>

          <div className="text-gray-500 font-semibold text-left">{t('profile.hobbies')}</div>
          <div className="text-gray-900 text-left">
            {isTranslating && needsTranslation ? (
              <span className="text-gray-400">{t('profile.translating')}</span>
            ) : (
              getDisplayValue('hobbies', profile.hobbies)
            )}
          </div>

          <div className="text-gray-500 font-semibold text-left">{t('profile.favoriteFood')}</div>
          <div className="text-gray-900 text-left">
            {isTranslating && needsTranslation ? (
              <span className="text-gray-400">{t('profile.translating')}</span>
            ) : (
              getDisplayValue('favorite_food', profile.favorite_food)
            )}
          </div>

          <div className="text-gray-500 font-semibold text-left">{t('profile.features')}</div>
          <div className="text-gray-900 text-left">
            {isTranslating && needsTranslation ? (
              <span className="text-gray-400">{t('profile.translating')}</span>
            ) : (
              getDisplayValue('features', profile.features)
            )}
          </div>

          <div className="text-gray-500 font-semibold text-left">{t('profile.bio')}</div>
          <div className="text-gray-900 text-left whitespace-pre-wrap">
            {isTranslating && needsTranslation ? (
              <span className="text-gray-400">{t('profile.translating')}</span>
            ) : (
              getDisplayValue('bio', profile.bio)
            )}
          </div>
        </div>
      </div>

      {/* Notice Section - Schedule Information */}
      {(noticePrefix || schedules.length > 0) && (
        <div className="bg-pink-50 border border-pink-300 p-4 mt-6 text-left">
          <h3 className="text-base font-semibold text-pink-800 mb-2 flex items-center gap-2 justify-start">
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            {t('profile.notice')}
          </h3>

          {/* Notice prefix */}
          {noticePrefix && (
            <p className="text-sm text-pink-900 leading-relaxed text-left whitespace-pre-wrap mb-3">
              {isTranslating && needsTranslation ? (
                <span className="text-pink-400">{t('profile.translating')}</span>
              ) : (
                needsTranslation && translatedProfile.notice_prefix
                  ? translatedProfile.notice_prefix
                  : noticePrefix
              )}
            </p>
          )}

          {/* Schedule list */}
          {schedules.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-pink-800">{t('profile.scheduleList')}</p>
              <ul className="text-sm text-pink-900 space-y-1">
                {schedules.map((schedule) => (
                  <li key={schedule.id} className="flex items-center gap-2">
                    <span className="text-pink-500">📅</span>
                    <span>{formatScheduleTimeRange(schedule.scheduled_start, schedule.scheduled_end, t)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* No schedules message */}
          {schedules.length === 0 && noticePrefix && (
            <p className="text-sm text-pink-700 italic">{t('profile.noSchedule')}</p>
          )}
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && profileImageUrl && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          onClick={() => setShowImageModal(false)}
        >
          <div className="max-w-4xl max-h-screen p-4">
            <img
              src={profileImageUrl}
              alt={t('profile.profileImage', { name: profile.display_name || profile.username })}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
          <button
            onClick={() => setShowImageModal(false)}
            className="absolute top-4 right-4 text-white text-4xl font-bold hover:text-gray-300"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
